import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { SessionService } from './session.service';
import {
  LoginRequest,
  LoginResponse,
  SessionData,
  UserLocation
} from '../models/user.model';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private http = inject(HttpClient);
  private session = inject(SessionService);
  private authServer = environment.authenticationServer;
  private entityServer = environment.entityService; // Assuming this is in your env
  private moduleId = environment.moduleId;

  // Legacy parity: expose `API` object that returns Promises like the AngularJS service
  public API = {
    Login: (credentials: LoginRequest) => firstValueFrom(this.login(credentials)),
    Logout: () => firstValueFrom(this.logout()),
    SetUserLocation: (location: UserLocation) => firstValueFrom(this.setUserLocation(location)),
    GetSession: () => firstValueFrom(this.getSession()),
    CreateSession: (data: Partial<LoginResponse> & { module_id?: string }) => firstValueFrom(this.createSession(data)),
    GetLoggedInUserDetails: (userId: string) => firstValueFrom(this.getLoggedInUserDetails(userId)),
    GetModuleAccessForUser: (moduleId: string | number) => firstValueFrom(this.getModuleAccessForUser(moduleId)),
    FindByCopyrightNoAuth: () => firstValueFrom(this.findByCopyrightNoAuth()),
    GetUserRole: (userId: string) => firstValueFrom(this.getUserRole(userId)),
    RemoveSessionId: (sessionId: string, moduleId?: string) =>
      firstValueFrom(this.removeSessionId(sessionId, moduleId || String(this.moduleId)))
  };

  // ─── Authentication ───────────────────────────────────────────────

  login(credentials: LoginRequest): Observable<{ status: number; data: LoginResponse }> {
    return this.http.post<LoginResponse>(
      `${this.authServer}/authentication/authenticate`,
      credentials,
      { observe: 'response' }
    ).pipe(
      map(res => ({ status: res.status, data: res.body! }))
    );
  }

  logout(): Observable<any> {
    return this.http.get(`${this.authServer}/api/user/logout`, {
      params: new HttpParams().set('module_id', this.moduleId)
    });
  }

  removeSessionId(sessionId: string, moduleId: string): Observable<any> {
    return this.http.get(`${this.authServer}/api/user/logout/${sessionId}`, {
      params: new HttpParams().set('module_id', moduleId)
    });
  }

  // ─── Session Management ────────────────────────────────────────────

  getSession(sessionId?: string): Observable<any> {
    const sessionKey = sessionId || this.session.get("user.session");
    return this.http.get(`${this.authServer}/session/${sessionKey}`);
  }

  createSession(data: any): Observable<any> {
    return this.http.post(`${this.authServer}/session/add-session`, data);
  }

  // ─── User Profile & Roles ──────────────────────────────────────────

  getLoggedInUserDetails(userId: string): Observable<any> {
    return this.http.get(`${this.authServer}/api/user/${userId}`);
  }

  getUserRole(userId: string): Observable<any> {
    return this.http.get(`${this.authServer}/api/user/getuserRole/${userId}`);
  }


  setUserLocation(location: UserLocation): Observable<any> {
    return this.http.put(`${this.authServer}/api/user/set/location`, location);
  }

  getModuleAccessForUser(moduleId: string | number): Observable<any> {
    return this.http.get(`${this.authServer}/api/access/role/module/${moduleId}`);
  }

  // ─── Miscellaneous ────────────────────────────────────────────────

  findByCopyrightNoAuth(): Observable<any> {
    return this.http.get(`${this.entityServer}/api/copyright/detailsnoauth`);
  }
}